import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  X, 
  Tags, 
  Send, 
  RefreshCw, 
  Power, 
  Download,
  Trash2,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { type Device } from "@/hooks/useDevices";
import { toast } from "sonner";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagRemovalDialog } from "@/components/tags/TagRemovalDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BulkActionToolbarProps {
  selectedDevices: Device[];
  onClearSelection: () => void;
  onRefresh?: () => void;
}

export const BulkActionToolbar: React.FC<BulkActionToolbarProps> = ({
  selectedDevices,
  onClearSelection,
  onRefresh
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [isTagRemovalDialogOpen, setIsTagRemovalDialogOpen] = useState(false);

  const handleBulkTagAssignment = () => {
    setIsTagDialogOpen(true);
  };

  const handleBulkTagRemoval = () => {
    setIsTagRemovalDialogOpen(true);
  };

  const handleBulkScriptDeployment = () => {
    toast.info(`Script deployment for ${selectedDevices.length} devices will open in a future update`);
  };

  const handleBulkAgentUpdate = async () => {
    setIsLoading(true);
    try {
      // Simulate bulk agent update
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success(`Agent update initiated for ${selectedDevices.length} devices`);
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to update agents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkUnenroll = async () => {
    setIsLoading(true);
    try {
      // Simulate bulk unenroll
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success(`${selectedDevices.length} devices unenrolled successfully`);
      onClearSelection();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to unenroll devices');
    } finally {
      setIsLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleExport = () => {
    // Create CSV data
    const csvData = selectedDevices.map(device => ({
      name: device.name,
      status: device.status,
      os_type: device.os_type,
      os_version: device.os_version,
      department: device.department || 'Unassigned',
      location: device.location || 'Unknown',
      customer: device.customer || 'Not Available',
      last_seen: device.last_seen || device.created_at
    }));

    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `devices-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success(`Exported ${selectedDevices.length} devices to CSV`);
  };

  return (
    <>
      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="px-3 py-1">
            {selectedDevices.length} selected
          </Badge>
          <Separator orientation="vertical" className="h-6" />
          
          {/* Quick Actions */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkTagAssignment}
              className="h-8"
            >
              <Tags className="mr-2 h-4 w-4" />
              Assign Tags
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkTagRemoval}
              className="h-8"
            >
              <X className="mr-2 h-4 w-4" />
              Remove Tags
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkScriptDeployment}
              className="h-8"
            >
              <Send className="mr-2 h-4 w-4" />
              Run Script
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkAgentUpdate}
              disabled={isLoading}
              className="h-8"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Update Agent
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8">
                  More Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Bulk Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={handleExport}>
                  <Download className="mr-2 h-4 w-4" />
                  Export to CSV
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem 
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Power className="mr-2 h-4 w-4" />
                  Unenroll Devices
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Unenroll Selected Devices
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unenroll {selectedDevices.length} device(s)? This action cannot be undone and will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Remove the devices from management</li>
                <li>Revoke all applied configurations</li>
                <li>Clear device enrollment data</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkUnenroll}
              disabled={isLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Unenrolling...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Unenroll Devices
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TagAssignmentDialog
        open={isTagDialogOpen}
        onOpenChange={setIsTagDialogOpen}
        resourceIds={selectedDevices.map(d => d.id)}
        resourceType="device"
        resourceName={`${selectedDevices.length} devices`}
        onAssignmentComplete={() => {
          setIsTagDialogOpen(false);
          onRefresh?.();
          toast.success(`Tags assigned to ${selectedDevices.length} devices`);
        }}
      />

      <TagRemovalDialog
        open={isTagRemovalDialogOpen}
        onOpenChange={setIsTagRemovalDialogOpen}
        deviceIds={selectedDevices.map(d => d.id)}
        deviceName={`${selectedDevices.length} devices`}
        onRemovalComplete={() => {
          setIsTagRemovalDialogOpen(false);
          onRefresh?.();
        }}
      />
    </>
  );
};